/**
 * lightbox.js
 * Full-screen image lightbox with gallery navigation, zoom, and actions.
 * Accessible (focus trap, ARIA, keyboard nav) and mobile-friendly (pinch zoom).
 */

/* ── SVG Icons ─────────────────────────────────────────────────── */

const ICON_CLOSE = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`;

const ICON_PREV = `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"></polyline></svg>`;

const ICON_NEXT = `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 6 15 12 9 18"></polyline></svg>`;

const ICON_COPY = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>`;

const ICON_DOWNLOAD = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>`;

const ICON_CHECK = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>`;

const ICON_ZOOM_IN = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line><line x1="11" y1="8" x2="11" y2="14"></line><line x1="8" y1="11" x2="14" y2="11"></line></svg>`;

const ICON_ZOOM_OUT = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line><line x1="8" y1="11" x2="14" y2="11"></line></svg>`;

/* ── Zoom constraints ──────────────────────────────────────────── */
const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.35;

/* ── Singleton state ───────────────────────────────────────────── */
let activeLightbox = null;

/**
 * Open the lightbox with the given image.
 *
 * @param {string}   imageSrc     - URL of the image to display
 * @param {string[]} allImages    - Array of all image URLs in the same message/gallery
 * @param {number}   currentIndex - Index of imageSrc within allImages
 */
export function openLightbox(imageSrc, allImages = [], currentIndex = 0) {
  // Prevent duplicates
  if (activeLightbox) closeLightbox();

  const images = allImages.length > 0 ? allImages : [imageSrc];
  let index = Math.max(0, Math.min(currentIndex, images.length - 1));
  let zoom = 1;
  let panX = 0;
  let panY = 0;
  let isPanning = false;
  let panStartX = 0;
  let panStartY = 0;
  let pinchStartDist = 0;
  let pinchStartZoom = 1;

  // Remember previously focused element for focus restoration
  const previousFocus = document.activeElement;

  /* ── Build DOM ──────────────────────────────────────────────── */

  const overlay = document.createElement('div');
  overlay.className = 'media-lightbox';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Image lightbox');
  overlay.tabIndex = -1;

  // Live region for screen reader announcements
  const liveRegion = document.createElement('div');
  liveRegion.className = 'media-lightbox-sr';
  liveRegion.setAttribute('role', 'status');
  liveRegion.setAttribute('aria-live', 'polite');
  overlay.appendChild(liveRegion);

  // Image viewport
  const viewport = document.createElement('div');
  viewport.className = 'media-lightbox-viewport';

  const imgEl = document.createElement('img');
  imgEl.className = 'media-lightbox-image';
  imgEl.alt = 'Lightbox image';
  imgEl.draggable = false;
  viewport.appendChild(imgEl);
  overlay.appendChild(viewport);

  // Top action bar
  const topBar = document.createElement('div');
  topBar.className = 'media-lightbox-topbar';
  topBar.innerHTML = `
    <div class="media-lightbox-actions">
      <button class="media-lightbox-btn" data-action="zoom-in" title="Zoom in" aria-label="Zoom in">${ICON_ZOOM_IN}</button>
      <button class="media-lightbox-btn" data-action="zoom-out" title="Zoom out" aria-label="Zoom out">${ICON_ZOOM_OUT}</button>
      <button class="media-lightbox-btn" data-action="copy" title="Copy image" aria-label="Copy image to clipboard">${ICON_COPY}</button>
      <button class="media-lightbox-btn" data-action="download" title="Download image" aria-label="Download image">${ICON_DOWNLOAD}</button>
    </div>
    <button class="media-lightbox-btn media-lightbox-btn--close" data-action="close" title="Close" aria-label="Close lightbox">${ICON_CLOSE}</button>
  `;
  overlay.appendChild(topBar);

  // Navigation arrows (only for multi-image galleries)
  let prevBtn = null;
  let nextBtn = null;
  if (images.length > 1) {
    prevBtn = document.createElement('button');
    prevBtn.className = 'media-lightbox-nav media-lightbox-nav--prev';
    prevBtn.setAttribute('aria-label', 'Previous image');
    prevBtn.innerHTML = ICON_PREV;
    overlay.appendChild(prevBtn);

    nextBtn = document.createElement('button');
    nextBtn.className = 'media-lightbox-nav media-lightbox-nav--next';
    nextBtn.setAttribute('aria-label', 'Next image');
    nextBtn.innerHTML = ICON_NEXT;
    overlay.appendChild(nextBtn);
  }

  // Dots indicator (only for multi-image galleries)
  let dotsWrap = null;
  if (images.length > 1) {
    dotsWrap = document.createElement('div');
    dotsWrap.className = 'media-lightbox-dots';
    dotsWrap.setAttribute('role', 'tablist');
    dotsWrap.setAttribute('aria-label', 'Image gallery navigation');
    images.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.className = 'media-lightbox-dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Image ${i + 1} of ${images.length}`);
      dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
      dot.addEventListener('click', () => goTo(i));
      dotsWrap.appendChild(dot);
    });
    overlay.appendChild(dotsWrap);
  }

  // Counter
  const counter = document.createElement('div');
  counter.className = 'media-lightbox-counter';
  counter.setAttribute('aria-hidden', 'true');
  overlay.appendChild(counter);

  document.body.appendChild(overlay);
  activeLightbox = overlay;

  // Trigger open animation
  requestAnimationFrame(() => {
    overlay.classList.add('media-lightbox--open');
    overlay.focus();
  });

  /* ── Core helpers ───────────────────────────────────────────── */

  function goTo(i) {
    index = ((i % images.length) + images.length) % images.length;
    resetZoom();
    loadImage();
    updateDots();
    announce(`Image ${index + 1} of ${images.length}`);
  }

  function loadImage() {
    imgEl.src = images[index];
    counter.textContent = images.length > 1 ? `${index + 1} / ${images.length}` : '';
    updateNavVisibility();
  }

  function updateDots() {
    if (!dotsWrap) return;
    const dots = dotsWrap.querySelectorAll('.media-lightbox-dot');
    dots.forEach((d, i) => {
      d.classList.toggle('media-lightbox-dot--active', i === index);
      d.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
  }

  function updateNavVisibility() {
    if (prevBtn) prevBtn.style.visibility = images.length > 1 ? 'visible' : 'hidden';
    if (nextBtn) nextBtn.style.visibility = images.length > 1 ? 'visible' : 'hidden';
  }

  function announce(text) {
    liveRegion.textContent = text;
  }

  /* ── Zoom & Pan ─────────────────────────────────────────────── */

  function setZoom(newZoom) {
    zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));
    if (zoom === 1) {
      panX = 0;
      panY = 0;
    }
    applyTransform();
  }

  function resetZoom() {
    zoom = 1;
    panX = 0;
    panY = 0;
    applyTransform();
  }

  function applyTransform() {
    imgEl.style.transform = `translate(${panX}px, ${panY}px) scale(${zoom})`;
    imgEl.classList.toggle('media-lightbox-image--zoomed', zoom > 1);
  }

  /* ── Event Handlers ─────────────────────────────────────────── */

  // Backdrop click closes
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay || e.target === viewport) {
      closeLightbox();
    }
  });

  // Top bar actions
  topBar.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-action]');
    if (!btn) return;
    e.stopPropagation();

    switch (btn.dataset.action) {
      case 'close':
        closeLightbox();
        break;
      case 'copy':
        copyImage(images[index], btn);
        break;
      case 'download':
        downloadImage(images[index]);
        break;
      case 'zoom-in':
        setZoom(zoom + ZOOM_STEP);
        break;
      case 'zoom-out':
        setZoom(zoom - ZOOM_STEP);
        break;
    }
  });

  // Navigation arrows
  if (prevBtn) prevBtn.addEventListener('click', (e) => { e.stopPropagation(); goTo(index - 1); });
  if (nextBtn) nextBtn.addEventListener('click', (e) => { e.stopPropagation(); goTo(index + 1); });

  // Keyboard navigation
  function onKeyDown(e) {
    switch (e.key) {
      case 'Escape':
        closeLightbox();
        break;
      case 'ArrowLeft':
        if (images.length > 1) goTo(index - 1);
        break;
      case 'ArrowRight':
        if (images.length > 1) goTo(index + 1);
        break;
      case '+':
      case '=':
        setZoom(zoom + ZOOM_STEP);
        break;
      case '-':
        setZoom(zoom - ZOOM_STEP);
        break;
      case '0':
        resetZoom();
        break;
      case 'Tab':
        trapFocus(e);
        break;
    }
  }
  overlay.addEventListener('keydown', onKeyDown);

  // Mouse wheel zoom
  viewport.addEventListener('wheel', (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setZoom(zoom + delta);
  }, { passive: false });

  // Double-click toggle zoom
  imgEl.addEventListener('dblclick', (e) => {
    e.stopPropagation();
    if (zoom > 1) {
      resetZoom();
    } else {
      setZoom(2.5);
    }
  });

  // Mouse drag for panning when zoomed
  imgEl.addEventListener('mousedown', (e) => {
    if (zoom <= 1) return;
    e.preventDefault();
    isPanning = true;
    panStartX = e.clientX - panX;
    panStartY = e.clientY - panY;
    imgEl.style.cursor = 'grabbing';
  });

  overlay.addEventListener('mousemove', (e) => {
    if (!isPanning) return;
    panX = e.clientX - panStartX;
    panY = e.clientY - panStartY;
    applyTransform();
  });

  overlay.addEventListener('mouseup', () => {
    isPanning = false;
    imgEl.style.cursor = zoom > 1 ? 'grab' : '';
  });

  // Touch: pinch zoom and pan
  viewport.addEventListener('touchstart', (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      pinchStartDist = getTouchDist(e.touches);
      pinchStartZoom = zoom;
    } else if (e.touches.length === 1 && zoom > 1) {
      isPanning = true;
      panStartX = e.touches[0].clientX - panX;
      panStartY = e.touches[0].clientY - panY;
    }
  }, { passive: false });

  viewport.addEventListener('touchmove', (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dist = getTouchDist(e.touches);
      const scale = dist / pinchStartDist;
      setZoom(pinchStartZoom * scale);
    } else if (e.touches.length === 1 && isPanning) {
      panX = e.touches[0].clientX - panStartX;
      panY = e.touches[0].clientY - panStartY;
      applyTransform();
    }
  }, { passive: false });

  viewport.addEventListener('touchend', () => {
    isPanning = false;
  });

  // Prevent body scroll while lightbox is open
  const origOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  /* ── Focus trap ─────────────────────────────────────────────── */

  function trapFocus(e) {
    const focusable = overlay.querySelectorAll(
      'button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* ── Close / Cleanup ────────────────────────────────────────── */

  function closeLightbox() {
    if (!activeLightbox) return;

    overlay.classList.remove('media-lightbox--open');
    overlay.classList.add('media-lightbox--closing');

    const onEnd = () => {
      overlay.remove();
      activeLightbox = null;
      document.body.style.overflow = origOverflow;
      if (previousFocus && typeof previousFocus.focus === 'function') {
        previousFocus.focus();
      }
    };

    overlay.addEventListener('animationend', onEnd, { once: true });
    // Fallback if animation doesn't fire (reduced-motion)
    setTimeout(onEnd, 400);
  }

  // Expose close globally for the outer module
  overlay._close = closeLightbox;

  /* ── Initialise ─────────────────────────────────────────────── */
  loadImage();
  updateDots();
}

/* ── Private Helpers ───────────────────────────────────────────── */

/**
 * Calculate distance between two touch points.
 * @param {TouchList} touches
 * @returns {number}
 */
function getTouchDist(touches) {
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Copy an image to the clipboard via canvas, with URL fallback.
 * @param {string} src
 * @param {HTMLElement} btn
 */
async function copyImage(src, btn) {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = src;

    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });

    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext('2d').drawImage(img, 0, 0);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (blob && navigator.clipboard && navigator.clipboard.write) {
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob }),
      ]);
    } else {
      await navigator.clipboard.writeText(src);
    }
  } catch {
    try { await navigator.clipboard.writeText(src); } catch { /* silent */ }
  }

  // Visual feedback
  if (btn) {
    const original = btn.innerHTML;
    btn.innerHTML = ICON_CHECK;
    btn.classList.add('media-lightbox-btn--success');
    setTimeout(() => {
      btn.innerHTML = original;
      btn.classList.remove('media-lightbox-btn--success');
    }, 1800);
  }
}

/**
 * Trigger a download for the given image URL.
 * @param {string} src
 */
function downloadImage(src) {
  const a = document.createElement('a');
  a.href = src;
  a.download = filenameFromUrl(src);
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Extract a filename from a URL with a reasonable fallback.
 * @param {string} url
 * @returns {string}
 */
function filenameFromUrl(url) {
  try {
    const pathname = new URL(url).pathname;
    const segments = pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1];
    if (last && /\.\w+$/.test(last)) return decodeURIComponent(last);
  } catch { /* ignore */ }
  return 'image.png';
}
